import React from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery';

const BORDER_SUBTLE = 'rgb(232, 224, 216)';
const CARD_BG = 'rgb(255, 253, 249)';

/** ~85% scale vs prior design — tighter cards without CTAs / dividers */
const CARD_PAD = 18;
const CARD_RADIUS = 12;
const ICON_SZ = 38;
const ICON_RADIUS = 9;
const ICON_EMOJI = 19;

export default function CommunitySection() {
  const narrow = useMediaQuery('(max-width: 680px)');

  const sectionPad = narrow ? '48px 16px' : '64px 48px';

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: narrow ? '1fr' : 'repeat(3, 1fr)',
    gap: narrow ? 10 : 12,
    maxWidth: 960,
    margin: '0 auto',
  };

  const cardShell = (accent, children) => (
    <div
      style={{
        background: CARD_BG,
        borderRadius: CARD_RADIUS,
        padding: CARD_PAD,
        border: `0.5px solid ${BORDER_SUBTLE}`,
        borderLeft: `2px solid ${accent}`,
        boxSizing: 'border-box',
      }}
    >
      {children}
    </div>
  );

  const iconBox = (accent, emoji) => (
    <div
      style={{
        width: ICON_SZ,
        height: ICON_SZ,
        borderRadius: ICON_RADIUS,
        background: accent,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: ICON_EMOJI,
        flexShrink: 0,
      }}
      aria-hidden
    >
      {emoji}
    </div>
  );

  const chipRow = (accentBg, accentColor, labels) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
      {labels.map((label) => (
        <span
          key={label}
          style={{
            background: accentBg,
            color: accentColor,
            borderRadius: 999,
            padding: '2px 8px',
            fontSize: 10,
            fontWeight: 500,
            fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
          }}
        >
          {label}
        </span>
      ))}
    </div>
  );

  const titleStyle = {
    margin: 0,
    fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--text)',
  };

  const badgeBase = {
    marginTop: 8,
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    padding: '2px 9px',
    fontSize: 9,
    fontWeight: 700,
    fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
  };

  const descStyle = {
    margin: '10px 0 0',
    fontSize: 13,
    lineHeight: 1.52,
    color: 'rgb(122, 107, 99)',
    fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
  };

  return (
    <section id="community" style={{ background: 'rgb(249, 246, 241)', padding: sectionPad, scrollMarginTop: 88 }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: 'var(--muted)',
            textTransform: 'uppercase',
            textAlign: 'center',
            marginBottom: 10,
          }}
        >
          Community · matched in real time
        </p>

        <h2
          className="serif"
          style={{
            fontSize: 'clamp(26px, 3vw, 32px)',
            fontWeight: 700,
            textAlign: 'center',
            color: 'var(--text)',
            lineHeight: 1.25,
            marginBottom: 10,
          }}
        >
          Real women.
          <br />
          <em style={{ color: 'var(--pink)', fontStyle: 'italic' }}>Same week as you.</em>
        </h2>

        <p
          style={{
            fontSize: 15,
            color: 'var(--muted)',
            textAlign: 'center',
            maxWidth: 580,
            margin: '0 auto 28px',
            lineHeight: 1.6,
          }}
        >
          Same week of stims. Same trimester. Same postpartum recovery. We connect you with women living exactly what you&apos;re
          living, right now.
        </p>

        <div style={gridStyle}>
          {cardShell(
            'rgb(251, 234, 240)',
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {iconBox('rgb(251, 234, 240)', '🏡')}
                <h3 style={titleStyle}>Orivon Spaces</h3>
              </div>
              <div
                style={{
                  ...badgeBase,
                  background: 'rgb(251, 234, 240)',
                  color: 'rgb(153, 53, 86)',
                }}
              >
                ✦ Matched to your chapter
              </div>
              <p style={{ ...descStyle, maxWidth: '38ch' }}>
                Ongoing communities tailored to your reproductive journey, where the conversations, support, and resources you find
                are relevant to where you are right now.
              </p>
              {chipRow('rgb(251, 234, 240)', 'rgb(153, 53, 86)', ['IVF Warriors', 'PCOS Sisters', 'After Loss'])}
            </>
          )}

          {cardShell(
            'rgb(225, 245, 238)',
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {iconBox('rgb(225, 245, 238)', '🎙️')}
                <h3 style={titleStyle}>Orivon Live</h3>
              </div>
              <div
                style={{
                  ...badgeBase,
                  gap: 5,
                  background: 'rgb(225, 245, 238)',
                  color: 'rgb(14, 110, 86)',
                }}
              >
                <span
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: 'rgb(34, 197, 94)',
                    flexShrink: 0,
                  }}
                  aria-hidden
                />
                Live now
              </div>
              <p style={descStyle}>
                Real-time rooms that open for the moments that can&apos;t wait — retrieval night, the two-week wait, a negative beta.
                The community shows up when you need it most.
              </p>
              {chipRow('rgb(225, 245, 238)', 'rgb(14, 110, 86)', ['Retrieval check-ins', 'TWW support', 'After a loss'])}
            </>
          )}

          {cardShell(
            'rgb(238, 237, 254)',
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {iconBox('rgb(238, 237, 254)', '🤝')}
                <h3 style={titleStyle}>Orivon Connect</h3>
              </div>
              <div
                style={{
                  ...badgeBase,
                  background: 'rgb(238, 237, 254)',
                  color: 'rgb(88, 58, 168)',
                }}
              >
                98% match accuracy
              </div>
              <p style={descStyle}>
                One-to-one matching with women at your exact stage. The platform knows your protocol, your chapter, your history —
                and finds the woman living it right alongside you.
              </p>
              {chipRow('rgb(238, 237, 254)', 'rgb(88, 58, 168)', ['Same protocol', 'Same week', 'Same chapter'])}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
